import React, { useState } from 'react';
import { ChevronLeft, Lock, Trash2, FileText, Shield, AlertTriangle, Key, Users, LogOut, Edit2, Plus, Mail, Crown, MessageSquare, Send, Bug, Lightbulb, Globe, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useUserProfile } from '../hooks/useUserProfile';
import { useSubscription } from '../hooks/useSubscription';
import { useChildProfile, ChildProfile } from '../contexts/ChildProfileContext';
import { useHeroes } from '../hooks/useHeroes';
import { Capacitor } from '@capacitor/core';
import { useMotionPermission } from '../contexts/MotionPermissionContext';
import LegalPageScreen from './LegalPageScreen';
import ParentalPin from './ParentalPin';
import ChildProfileEditor from './ChildProfileEditor';
import SubscriptionScreen from './SubscriptionScreen';
import { useLanguageContext } from '../contexts/LanguageContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Language } from '../hooks/useLanguage';
import LanguageModal from './LanguageModal';

interface ParentSettingsScreenProps {
  onBack: () => void;
}

const ParentSettingsScreen: React.FC<ParentSettingsScreenProps> = ({ onBack }) => {
  const { user, profile, signOut, updateProfile, authProvider } = useUserProfile();
  const { hasPremiumAccess, isInTrial, entitlement } = useSubscription(user?.id);
  const { children: childProfiles, canAddMoreChildren, setActiveChild } = useChildProfile();
  const { heroes } = useHeroes();
  const isNative = Capacitor.isNativePlatform();
  const { language } = useLanguageContext();
  const { t } = useTranslation();

  // Language options
  const languages: { code: Language; name: string; flag: string }[] = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' }
  ];
  const currentLanguage = languages.find(l => l.code === language) || languages[0];
  const {
    motionEnabled,
    setMotionEnabled,
    motionPermission,
    requestMotionPermission,
    needsMotionPermission,
    setShowPermissionModal
  } = useMotionPermission();

  // Get a hero image for a child - uses their selected hero or first available hero
  const getChildAvatar = (child: ChildProfile): string | null => {
    if (child.avatarHeroId) {
      const selectedHero = heroes.find(h => h.id === child.avatarHeroId);
      if (selectedHero?.emoji) return selectedHero.emoji;
    }
    if (heroes.length > 0 && heroes[0].emoji) {
      return heroes[0].emoji;
    }
    return null;
  };

  // Change password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Legal pages state
  const [showLegalPage, setShowLegalPage] = useState<'terms' | 'privacy' | null>(null);

  // PIN parental state
  const [showPinModal, setShowPinModal] = useState<'setup' | 'change' | null>(null);
  const [pinSuccess, setPinSuccess] = useState(false);

  // Child profile editing state
  const [showChildEditor, setShowChildEditor] = useState(false);
  const [editingChild, setEditingChild] = useState<ChildProfile | undefined>(undefined);

  // Newsletter state
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  // Subscription screen state
  const [showSubscriptionScreen, setShowSubscriptionScreen] = useState(false);

  // Feedback form state
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'suggestion' | 'bug'>('suggestion');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  // Language modal state
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  // Check if PIN is configured
  const hasPinConfigured = !!profile?.parentPinHash;

  // Check if user has OAuth provider (no password to change)
  const isOAuthUser = user?.app_metadata?.provider && user.app_metadata.provider !== 'email';

  // Check if user has active subscription (from subscriptions table)
  const hasActiveSubscription = hasPremiumAccess;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setPasswordLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (err: any) {
      console.error('Erreur changement mot de passe:', err);
      setPasswordError(err.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      setDeleteError('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      // Call edge function to delete user (needs service role)
      const { error } = await supabase.functions.invoke('delete-user', {
        body: { userId: user?.id }
      });

      if (error) throw error;

      // Sign out and redirect
      await signOut();
      window.location.reload();
    } catch (err: any) {
      console.error('Erreur suppression compte:', err);
      setDeleteError(err.message || 'Erreur lors de la suppression du compte');
      setDeleteLoading(false);
    }
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackMessage.trim()) {
      setFeedbackError('Veuillez entrer un message');
      return;
    }

    setFeedbackLoading(true);
    setFeedbackError(null);

    try {
      // Insert feedback into Supabase
      const { error } = await supabase
        .from('feedback')
        .insert({
          user_id: user?.id,
          type: feedbackType,
          message: feedbackMessage.trim(),
          user_email: user?.email,
          platform: Capacitor.getPlatform(),
          app_version: '1.0.0',
        });

      if (error) throw error;

      setFeedbackSuccess(true);
      setFeedbackMessage('');
      setShowFeedbackForm(false);

      // Reset success message after 3 seconds
      setTimeout(() => setFeedbackSuccess(false), 3000);
    } catch (err: any) {
      console.error('Erreur envoi feedback:', err);
      setFeedbackError(err.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Show legal page if selected
  if (showLegalPage) {
    return (
      <LegalPageScreen
        slug={showLegalPage}
        onBack={() => setShowLegalPage(null)}
      />
    );
  }

  // Show child editor if editing
  if (showChildEditor) {
    return (
      <ChildProfileEditor
        child={editingChild}
        onSave={() => {
          setShowChildEditor(false);
          setEditingChild(undefined);
        }}
        onCancel={() => {
          setShowChildEditor(false);
          setEditingChild(undefined);
        }}
        onDelete={() => {
          setShowChildEditor(false);
          setEditingChild(undefined);
        }}
      />
    );
  }

  // Show subscription screen
  if (showSubscriptionScreen) {
    return (
      <SubscriptionScreen
        onBack={() => setShowSubscriptionScreen(false)}
        onSubscriptionComplete={async (plan) => {
          if (!user) return;

          const now = new Date();

          // Trial period: 7 days from now (for iOS refund period)
          const trialEndsAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

          // Subscription expiration based on plan
          const currentPeriodEnd = plan === 'yearly'
            ? new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
            : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

          // Update subscriptions table (in production, this would be done via Edge Function/Webhook)
          const { error } = await supabase
            .from('subscriptions')
            .upsert({
              user_id: user.id,
              status: 'trial',
              tier: 'premium',
              plan: plan,
              trial_ends_at: trialEndsAt.toISOString(),
              current_period_start: now.toISOString(),
              current_period_end: currentPeriodEnd.toISOString(),
              provider: 'stripe', // Placeholder - will be set by payment provider
            }, {
              onConflict: 'user_id'
            });

          if (error) {
            console.error('Error updating subscription:', error);
          }

          // Close subscription screen and reload to get updated subscription
          setShowSubscriptionScreen(false);
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div className={`h-screen-adaptive bg-gradient-to-b from-gray-50 to-gray-100 ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">{t("settings.settings", "Settings")}</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Account Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">{t("settings.compte", "Compte")}</h2>
          </div>

          {/* Language Selector */}
          <button
            onClick={() => setShowLanguageModal(true)}
            className="w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Globe size={20} className="text-blue-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-800">{t("settings.language", "Language")}</p>
                <p className="text-sm text-gray-500">{t("settings.choose_the_app_language", "Choose the app language")}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">{currentLanguage.flag}</span>
                <span className="text-sm font-medium text-gray-600">{currentLanguage.name}</span>
                <ChevronRight size={18} className="text-gray-400" />
              </div>
            </div>
          </button>

          {/* Change Password */}
          {!isOAuthUser ? (
            <div className="p-4 border-b border-gray-100">
              <button
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="w-full flex items-center gap-3 text-left"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Lock size={20} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{t("settings.changer_le_mot_de_passe", "Changer le mot de passe")}</p>
                  <p className="text-sm text-gray-500">{t("settings.modifier_votre_mot_de_passe_actuel", "Modifier votre mot de passe actuel")}</p>
                </div>
              </button>

              {showPasswordForm && (
                <form onSubmit={handleChangePassword} className="mt-4 space-y-3">
                  {passwordError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                      {passwordError}
                    </div>
                  )}
                  {passwordSuccess && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
                      {t("settings.mot_de_passe_modifie_avec_succes", "Password changed successfully!")}
                    </div>
                  )}
                  <input
                    type="password"
                    placeholder={t("settings.nouveau_mot_de_passe", "Nouveau mot de passe")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength={6}
                  />
                  <input
                    type="password"
                    placeholder={t("settings.confirm_password", "Confirm password")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPasswordForm(false)}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      {t("settings.cancel", "Cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {passwordLoading ? t("settings.modification", "Modification...") : t("settings.edit", "Edit")}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Lock size={20} className="text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-400">{t("settings.changer_le_mot_de_passe", "Changer le mot de passe")}</p>
                  <p className="text-sm text-gray-400">
                    {t("settings.connecte_via", "Connected via")} {user?.app_metadata?.provider === 'google' ? 'Google' : user?.app_metadata?.provider === 'apple' ? 'Apple' : 'OAuth'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Logout */}
          <div className="p-4">
            <button
              onClick={() => {
                onBack();
                signOut();
              }}
              className="w-full flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <LogOut size={20} className="text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">{t("settings.se_deconnecter", "Log out")}</p>
                <p className="text-sm text-gray-500">{t("settings.deconnexion_du_compte_parent", "Log out from parent account")}</p>
              </div>
            </button>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">{t("settings.subscription", "Subscription")}</h2>
          </div>

          <div className="p-4">
            <button
              onClick={() => setShowSubscriptionScreen(true)}
              className="w-full flex items-center gap-3 text-left"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasActiveSubscription ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-gray-100'}`}>
                <Crown size={20} className={hasActiveSubscription ? 'text-white' : 'text-gray-500'} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">
                  {hasActiveSubscription ? 'LilHero Premium' : t("settings.passer_a_premium", "Upgrade to Premium")}
                </p>
                <p className="text-sm text-gray-500">
                  {hasActiveSubscription
                    ? t("settings.abonnement_actif", "Abonnement actif")
                    : isInTrial
                      ? t("settings.essai_gratuit", "Essai gratuit")
                      : t("settings.unlimited_access_to_all_stories", "Unlimited access to all stories")
                  }
                </p>
              </div>
              {hasActiveSubscription ? (
                <span className="text-xs font-medium text-white bg-gradient-to-r from-yellow-400 to-orange-500 px-2 py-1 rounded-full">
                  Premium
                </span>
              ) : isInTrial ? (
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  {t("settings.essai", "Essai")}
                </span>
              ) : null}
            </button>
          </div>
        </div>

        {/* Parental PIN Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">{t("settings.controle_parental", "Parental controls")}</h2>
          </div>

          <div className="p-4">
            <button
              onClick={() => setShowPinModal(hasPinConfigured ? 'change' : 'setup')}
              className="w-full flex items-center gap-3 text-left"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasPinConfigured ? 'bg-green-100' : 'bg-orange-100'}`}>
                <Key size={20} className={hasPinConfigured ? 'text-green-600' : 'text-orange-600'} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">{t("settings.parental_pin_code", "Parental PIN code")}</p>
                <p className="text-sm text-gray-500">
                  {hasPinConfigured
                    ? t("settings.protects_access_to_settings", "Protects access to settings")
                    : t("settings.non_configure_cliquez_pour_activer", "Not configured - Click to enable")
                  }
                </p>
              </div>
              {hasPinConfigured && (
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  {t("settings.actif", "Actif")}
                </span>
              )}
            </button>

            {pinSuccess && (
              <div className="mt-3 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
                {t("settings.code_pin_modifie_avec_succes", "PIN code changed successfully!")}
              </div>
            )}
          </div>
        </div>

        {/* Child Profiles Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-700">{t("settings.profils_enfants", "Profils enfants")}</h2>
            <span className="text-sm text-gray-500">{childProfiles.length}/3</span>
          </div>

          {/* List of children */}
          {childProfiles.map((child) => {
            const avatarUrl = getChildAvatar(child);
            return (
            <div
              key={child.id}
              className="p-4 flex items-center gap-3 border-b border-gray-100"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center shadow overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={child.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg font-bold text-white">
                    {child.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">{child.name}</p>
                {child.birthDate && (
                  <p className="text-sm text-gray-500">
                    {new Date().getFullYear() - new Date(child.birthDate).getFullYear()} ans
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setEditingChild(child);
                  setShowChildEditor(true);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Edit2 size={18} className="text-gray-500" />
              </button>
            </div>
          );
          })}

          {/* Add child button */}
          {canAddMoreChildren ? (
            <button
              onClick={() => {
                setEditingChild(undefined);
                setShowChildEditor(true);
              }}
              className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50"
            >
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                <Plus size={24} className="text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-600">{t("settings.ajouter_un_profil", "Ajouter un profil")}</p>
                <p className="text-sm text-gray-400">{t("settings.maximum_3_profils_par_compte", "Maximum 3 profils par compte")}</p>
              </div>
            </button>
          ) : (
            <div className="p-4 flex items-center gap-3 text-gray-400">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Users size={20} className="text-gray-300" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{t("settings.limite_atteinte", "Limite atteinte")}</p>
                <p className="text-sm">{t("settings.maximum_3_profils_par_compte", "Maximum 3 profils par compte")}</p>
              </div>
            </div>
          )}
        </div>

        {/* Preferences Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">{t("settings.preferences", "Preferences")}</h2>
          </div>

          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Mail size={20} className="text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">{t("settings.newsletter", "Newsletter")}</p>
                <p className="text-sm text-gray-500">{t("settings.recevoir_les_nouveautes_et_offres", "Receive news and offers")}</p>
              </div>
              <button
                onClick={async () => {
                  if (newsletterLoading) return;
                  setNewsletterLoading(true);
                  try {
                    const newValue = !profile?.newsletterOptedIn;
                    await updateProfile({
                      newsletterOptedIn: newValue,
                      newsletterOptedInAt: newValue ? new Date().toISOString() : null,
                    });
                  } catch (err) {
                    console.error('Error updating newsletter preference:', err);
                  } finally {
                    setNewsletterLoading(false);
                  }
                }}
                disabled={newsletterLoading}
                className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${
                  profile?.newsletterOptedIn ? 'bg-purple-500' : 'bg-gray-300'
                } ${newsletterLoading ? 'opacity-50' : ''}`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    profile?.newsletterOptedIn ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Motion Effects Toggle - Only show on native */}
          {isNative && (
            <div className="p-4 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                  <span className="text-xl">📱</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{t("settings.effets_de_mouvement", "Effets de mouvement")}</p>
                  <p className="text-sm text-gray-500">
                    {motionPermission === 'granted' || motionPermission === 'not_needed'
                      ? t("settings.parallaxe_avec_le_gyroscope", "Parallaxe avec le gyroscope")
                      : motionPermission === 'denied'
                        ? t("settings.permission_refusee", "Permission denied")
                        : t("settings.permission_requise", "Permission requise")}
                  </p>
                </div>
                {motionPermission === 'pending' && needsMotionPermission ? (
                  <button
                    onClick={() => setShowPermissionModal(true)}
                    className="px-3 py-1.5 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    {t("settings.activer", "Activer")}
                  </button>
                ) : (
                  <button
                    onClick={() => setMotionEnabled(!motionEnabled)}
                    disabled={motionPermission === 'denied'}
                    className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${
                      motionEnabled && motionPermission !== 'denied' ? 'bg-pink-500' : 'bg-gray-300'
                    } ${motionPermission === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        motionEnabled && motionPermission !== 'denied' ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Legal Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">{t("settings.legal", "Legal")}</h2>
          </div>

          <button
            onClick={() => setShowLegalPage('terms')}
            className="w-full p-4 flex items-center gap-3 text-left border-b border-gray-100 hover:bg-gray-50"
          >
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <FileText size={20} className="text-gray-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-800">{t("settings.terms_of_use", "Terms of use")}</p>
            </div>
          </button>

          <button
            onClick={() => setShowLegalPage('privacy')}
            className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50"
          >
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Shield size={20} className="text-gray-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-800">{t("settings.privacy_policy", "Privacy policy")}</p>
            </div>
          </button>
        </div>

        {/* Contact & Support Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">{t("settings.contact_support", "Contact & Support")}</h2>
          </div>

          <div className="p-4">
            <button
              onClick={() => setShowFeedbackForm(!showFeedbackForm)}
              className="w-full flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <MessageSquare size={20} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">{t("settings.nous_contacter", "Nous contacter")}</p>
                <p className="text-sm text-gray-500">{t("settings.suggestion_bug_ou_question", "Suggestion, bug ou question")}</p>
              </div>
            </button>

            {feedbackSuccess && (
              <div className="mt-3 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm flex items-center gap-2">
                <Send size={16} />
                {t("settings.message_envoye_avec_succes_merci_pour_votre_retour", "Message sent successfully! Thanks for your feedback.")}
              </div>
            )}

            {showFeedbackForm && (
              <form onSubmit={handleSubmitFeedback} className="mt-4 space-y-4">
                {/* Type selector */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFeedbackType('suggestion')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                      feedbackType === 'suggestion'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Lightbulb size={18} />
                    <span className="text-sm font-medium">{t("settings.suggestion", "Suggestion")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFeedbackType('bug')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border-2 transition-all ${
                      feedbackType === 'bug'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Bug size={18} />
                    <span className="text-sm font-medium">Bug</span>
                  </button>
                </div>

                {/* Message textarea */}
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder={feedbackType === 'bug'
                    ? t("settings.decrivez_le_probleme_rencontre", "Describe the issue...")
                    : t("settings.partagez_votre_idee_ou_suggestion", "Share your idea or suggestion...")
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />

                {feedbackError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                    {feedbackError}
                  </div>
                )}

                {/* Submit buttons */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setFeedbackMessage('');
                      setFeedbackError(null);
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    {t("settings.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={feedbackLoading || !feedbackMessage.trim()}
                    className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {feedbackLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        {t("settings.envoi", "Envoi...")}
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        {t("settings.envoyer", "Envoyer")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Delete Account - At the very bottom */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4">
            <button
              onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
              className="w-full flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-red-600">{t("settings.supprimer_le_compte", "Supprimer le compte")}</p>
                <p className="text-sm text-gray-500">{t("settings.supprimer_definitivement_votre_compte_et_vos_donne", "Permanently delete your account and data")}</p>
              </div>
            </button>

            {showDeleteConfirm && (
              <div className="mt-4 space-y-3">
                {/* Warning if active subscription */}
                {hasActiveSubscription && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertTriangle size={24} className="text-amber-600 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-amber-800">{t("settings.abonnement_actif_detecte", "Active subscription detected")}</p>
                        <p className="text-sm text-amber-700 mt-1">
                          {t("settings.la_suppression_de_votre_compte_nannule_p", "La suppression de votre compte n'annule pas votre abonnement.")}
                        </p>
                        <p className="text-sm text-amber-700 mt-2">
                          <strong>{t("settings.pour_annuler", "Pour annuler :")}</strong>
                        </p>
                        {Capacitor.getPlatform() === 'ios' ? (
                          <p className="text-sm text-amber-700">
                            {t("settings.reglages_iphone_votre_nom_abonnements_lilhero", "iPhone Settings → [your name] → Subscriptions → LilHero")}
                          </p>
                        ) : Capacitor.getPlatform() === 'android' ? (
                          <p className="text-sm text-amber-700">
                            {t("settings.google_play_menu_abonnements_lilhero", "Google Play → Menu → Abonnements → LilHero")}
                          </p>
                        ) : (
                          <p className="text-sm text-amber-700">
                            {t("settings.gerez_votre_abonnement_dans_les_parametres_de_votr", "Manage your subscription in your store settings")}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700 mb-3">
                    {t("settings.cette_action_est_irreversible_toutes_vos_donnees_s", "This action is irreversible. All your data will be deleted:")}
                  </p>
                  <ul className="text-sm text-red-700 list-disc list-inside space-y-1 mb-3">
                    <li>{t("settings.votre_progression_de_lecture", "Votre progression de lecture")}</li>
                    <li>{t("settings.vos_objets_collectes", "Your collected objects")}</li>
                    <li>{t("settings.vos_personnalisations", "Vos personnalisations")}</li>
                  </ul>
                  <p className="text-sm text-red-700 font-medium">
                    {t("settings.tapez_supprimer_pour_confirmer", "Tapez SUPPRIMER pour confirmer :")}
                  </p>
                </div>

                {deleteError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                    {deleteError}
                  </div>
                )}

                <input
                  type="text"
                  placeholder={t("settings.supprimer", "SUPPRIMER")}
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                      setDeleteError(null);
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    {t("settings.cancel", "Cancel")}
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || deleteConfirmText !== 'SUPPRIMER'}
                    className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteLoading ? t("settings.suppression", "Suppression...") : t("settings.delete", "Delete")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* App Info */}
        <div className="text-center text-sm text-gray-400 py-4">
          <p>LilHero v1.0.0</p>
          <p>© 2024 LilHero Universe</p>
        </div>
      </div>

      {/* PIN Modal */}
      {showPinModal && (
        <ParentalPin
          mode={showPinModal}
          currentPinHash={profile?.parentPinHash}
          userEmail={user?.email}
          authProvider={authProvider}
          onSuccess={() => {
            setShowPinModal(null);
            setPinSuccess(true);
            setTimeout(() => setPinSuccess(false), 3000);
          }}
          onCancel={() => setShowPinModal(null)}
          onPinSet={async (pinHash) => {
            await updateProfile({ parentPinHash: pinHash });
          }}
        />
      )}

      {/* Language Modal */}
      <LanguageModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />
    </div>
  );
};

export default ParentSettingsScreen;
